import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useWidgetErrorBanner } from '../../../hooks/widget/useWidgetErrorBanner'
import { useTotalRevenueWidget } from '../../../hooks/widget/reports/useTotalRevenueWidget'
import { useAveragePriceWidget } from '../../../hooks/widget/reports/useAveragePriceWidget'
import { useActiveMembersWidget } from '../../../hooks/widget/reports/useActiveMembersWidget'
import { useTotalReservationsWidget } from '../../../hooks/widget/reports/useTotalReservationsWidget'
import { useReservationsChartWidget } from '../../../hooks/widget/reports/useReservationsChartWidget'
import { useTopCourtsWidget } from '../../../hooks/widget/reports/useTopCourtsWidget'
import { useRevenueSplitWidget } from '../../../hooks/widget/reports/useRevenueSplitWidget'
import { usePeakHoursWidget } from '../../../hooks/widget/reports/usePeakHoursWidget'

import KPI from '../../../components/ui/kpi/KPI'
import { Widget } from '../../../components/widget/Widget'
import { WidgetGrid } from '../../../components/widget/WidgetGrid'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import {
  ReportFilters,
  ReportPeriodOption,
} from '../../../components/authenticated/management/reports/reportFilters/ReportFilters'
import { ReservationsChart } from '../../../components/authenticated/management/reports/reservationsChart/ReservationsChart'
import { TopCourts } from '../../../components/authenticated/management/reports/topCourts/TopCourts'
import { RevenueSplit } from '../../../components/authenticated/management/reports/revenueSplit/RevenueSplit'
import { PeakHours } from '../../../components/authenticated/management/reports/peakHours/PeakHours'

import { PeakHoursWidget, WidgetTone } from '../../../types/widget/widget'
import {
  MonthlyReservationsReport,
  ReportIntervalInput,
  ReportPeriod,
} from '../../../types/report/report'
import {
  RevenueSplitModel,
  TopCourtReportModel,
} from '../../../types/court/court'

import {
  createCurrencyFormatter,
  createDateFormatter,
  createNumberFormatter,
  getIntlLocale,
} from '../../../utils/locale/localeUtils'
import {
  createReportInterval,
  isReportIntervalInputValid,
} from '../../../utils/reportUtils'

const INITIAL_REPORT_FILTERS: ReportIntervalInput = {
  period: ReportPeriod.LAST_MONTH,
  startDate: null,
  endDate: null,
}

export const AdminReports = () => {
  const { t, i18n } = useTranslation('reports')
  const [filters, setFilters] = useState<ReportIntervalInput>(() => ({
    ...INITIAL_REPORT_FILTERS,
  }))
  const [appliedFilters, setAppliedFilters] = useState<ReportIntervalInput>(
    () => ({ ...INITIAL_REPORT_FILTERS })
  )
  const {
    period: selectedPeriod,
    startDate: appliedStartDate,
    endDate: appliedEndDate,
  } = appliedFilters

  const reportInterval = useMemo(
    () => createReportInterval(appliedFilters),
    [appliedFilters]
  )
  const reservationsChartInterval = useMemo(
    () =>
      selectedPeriod === ReportPeriod.TODAY
        ? createReportInterval({
            period: ReportPeriod.THIS_MONTH,
            startDate: null,
            endDate: null,
          })
        : reportInterval,
    [reportInterval, selectedPeriod]
  )

  const handleFiltersChange = (nextFilters: ReportIntervalInput) => {
    setFilters(nextFilters)

    if (!isReportIntervalInputValid(nextFilters)) {
      return
    }

    const nextInterval = createReportInterval(nextFilters)
    if (nextInterval.startDate <= nextInterval.endDate) {
      setAppliedFilters(nextFilters)
    }
  }

  const totalReservationsResult = useTotalReservationsWidget(reportInterval)
  const totalRevenueResult = useTotalRevenueWidget(reportInterval)
  const activeMembersResult = useActiveMembersWidget(reportInterval)
  const averagePriceResult = useAveragePriceWidget(reportInterval)
  const reservationsChartResult = useReservationsChartWidget(
    reservationsChartInterval
  )
  const topCourtsResult = useTopCourtsWidget(reportInterval)
  const revenueSplitResult = useRevenueSplitWidget(reportInterval)
  const peakHoursResult = usePeakHoursWidget(reportInterval)
  const handleWidgetError = useWidgetErrorBanner()

  const locale = getIntlLocale(i18n.resolvedLanguage)
  const numberFormatter = createNumberFormatter(locale)
  const currencyFormatter = createCurrencyFormatter(locale, {
    maximumFractionDigits: 0,
  })
  const periodDateFormatter = createDateFormatter(locale, {
    dateStyle: 'medium',
  })
  const comparisonLabel = t('reports.kpi.comparison')
  const periodOptions: ReportPeriodOption[] = [
    {
      value: ReportPeriod.CUSTOM,
      label: t('reports.filters.custom'),
      shortLabel: t('reports.filters.short.custom'),
    },
    {
      value: ReportPeriod.TODAY,
      label: t('reports.filters.today'),
      shortLabel: t('reports.filters.short.today'),
    },
    {
      value: ReportPeriod.THIS_MONTH,
      label: t('reports.filters.this-month'),
      shortLabel: t('reports.filters.short.this-month'),
    },
    {
      value: ReportPeriod.LAST_MONTH,
      label: t('reports.filters.last-month'),
      shortLabel: t('reports.filters.short.last-month'),
    },
    {
      value: ReportPeriod.LAST_3_MONTHS,
      label: t('reports.filters.last-three-months'),
      shortLabel: t('reports.filters.short.last-three-months'),
    },
    {
      value: ReportPeriod.LAST_6_MONTHS,
      label: t('reports.filters.last-six-months'),
      shortLabel: t('reports.filters.short.last-six-months'),
    },
    {
      value: ReportPeriod.LAST_12_MONTHS,
      label: t('reports.filters.last-twelve-months'),
      shortLabel: t('reports.filters.short.last-twelve-months'),
    },
  ]
  const selectedPeriodOption = periodOptions.find(
    option => option.value === selectedPeriod
  )
  const customPeriodSelected =
    selectedPeriod === ReportPeriod.CUSTOM && appliedStartDate && appliedEndDate
  const selectedPeriodLabel = customPeriodSelected
    ? `${periodDateFormatter.format(
        appliedStartDate
      )} – ${periodDateFormatter.format(appliedEndDate)}`
    : (selectedPeriodOption?.label ?? '')

  return (
    <div className="admin-reports">
      <PageHeader
        title={{
          eyebrow: t('reports.page.eyebrow', {
            period: selectedPeriodLabel,
          }),
          h1: t('reports.page.title'),
        }}
        sub={t('reports.page.subtitle')}
        actions={[
          <ReportFilters
            key="report-filters"
            value={filters}
            periodOptions={periodOptions}
            onChange={handleFiltersChange}
          />,
        ]}
      />
      <WidgetGrid>
        <Widget
          result={totalReservationsResult}
          className="col-12 col-md-6 col-xl-3"
          tone={WidgetTone.DARK}
          onError={handleWidgetError}
        >
          {data => (
            <KPI
              label={t('reports.kpi.reservations')}
              value={data ? numberFormatter.format(data.value) : '—'}
              delta={data?.changePercentage ?? undefined}
              sub={comparisonLabel}
              deltaFallback={
                data ? t('reports.kpi.no-previous-data') : undefined
              }
              tone="dark"
            />
          )}
        </Widget>
        <Widget
          result={totalRevenueResult}
          className="col-12 col-md-6 col-xl-3"
          tone={WidgetTone.ACCENT}
          onError={handleWidgetError}
        >
          {data => (
            <KPI
              label={t('reports.kpi.revenue')}
              value={data ? currencyFormatter.format(data.value) : '—'}
              delta={data?.changePercentage ?? undefined}
              sub={comparisonLabel}
              deltaFallback={
                data ? t('reports.kpi.no-previous-data') : undefined
              }
              tone="accent"
            />
          )}
        </Widget>
        <Widget
          result={activeMembersResult}
          className="col-12 col-md-6 col-xl-3"
          onError={handleWidgetError}
        >
          {data => (
            <KPI
              label={t('reports.kpi.active-members')}
              value={data ? numberFormatter.format(data.value) : '—'}
              delta={data?.changePercentage ?? undefined}
              sub={comparisonLabel}
              deltaFallback={
                data ? t('reports.kpi.no-previous-data') : undefined
              }
            />
          )}
        </Widget>
        <Widget
          result={averagePriceResult}
          className="col-12 col-md-6 col-xl-3"
          onError={handleWidgetError}
        >
          {data => (
            <KPI
              label={t('reports.kpi.average-price')}
              value={data ? currencyFormatter.format(data.value) : '—'}
              delta={data?.changePercentage ?? undefined}
              sub={comparisonLabel}
              deltaFallback={
                data ? t('reports.kpi.no-previous-data') : undefined
              }
            />
          )}
        </Widget>
        <Widget<MonthlyReservationsReport[]>
          result={reservationsChartResult}
          title={t('reports.reservations.title')}
          className="col-12 col-xxl-7 reservations-chart-widget"
          onError={handleWidgetError}
        >
          {data =>
            data ? <ReservationsChart reports={data} locale={locale} /> : null
          }
        </Widget>
        <Widget<TopCourtReportModel[]>
          result={topCourtsResult}
          title={t('reports.top-courts.title')}
          className="col-12 col-xxl-5"
          onError={handleWidgetError}
        >
          {data => (data ? <TopCourts courts={data} locale={locale} /> : null)}
        </Widget>
        <Widget<RevenueSplitModel>
          result={revenueSplitResult}
          title={t('reports.revenue.title')}
          className="col-12 col-md-6"
          onError={handleWidgetError}
        >
          {data =>
            data ? <RevenueSplit revenue={data} locale={locale} /> : null
          }
        </Widget>
        <Widget<PeakHoursWidget>
          result={peakHoursResult}
          title={t('reports.peak-hours.title')}
          className="col-12 col-md-6"
          tone={WidgetTone.DARK}
          onError={handleWidgetError}
        >
          {data => (data ? <PeakHours report={data} locale={locale} /> : null)}
        </Widget>
      </WidgetGrid>
    </div>
  )
}
